import { FC, memo } from 'react';
import { Link } from 'react-router-dom';
import { Box, Divider, HStack } from '@chakra-ui/react';

import { TransactionPaymentMethod } from '__generated__/GQL';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import Tooltip from 'components/_core/Tooltip';
import Loader from 'components/Loader';
import NavigationHeader from 'components/NavigationHeader';
import { logEvent } from 'features/Analytics/analytics';
import TransactionAmount from 'features/Bank/components/TransactionAmount';
import { useDisputableTransactions } from 'features/Bank/pages/Dispute/hooks/useDisputableTransactions';
import { getDisputePath } from 'features/Bank/routes';
import { formatDate, PP } from 'helpers/date';

import { useBankOverviewSearchParams } from '../../hooks/useBankOverviewSearchParams';
import { useShouldMergeIntraCompanyFundsTransfers } from '../../hooks/useShouldMergeIntraCompanyFundsTransfers';
import TransactionIcon from '../../Transactions/TransactionIcon';
import { TransactionLabelBuilder } from '../../utils';
import { DetailedTransaction } from '../hooks/useDetailedTransactionQuery';
import locales from './locales';

interface HelpSidebarProps {
  transaction: DetailedTransaction;
}

const Help: FC<HelpSidebarProps> = ({ transaction }) => {
  const { getRootPath, getTransactionPath } = useBankOverviewSearchParams();

  const { paymentMethod, transactionAt, transactionId } = transaction;

  const {
    disputableTransactions: [disputableTransaction],
    isLoading,
  } = useDisputableTransactions([transactionId]);

  const shouldDisplayDisputeButton = Boolean(disputableTransaction);
  const isAlreadyDisputed = Boolean(disputableTransaction?.isAlreadyDisputed);
  const alreadyDisputedAt = disputableTransaction?.alreadyDisputedAt;

  const isMergeIntraCompanyFundsTransfer =
    useShouldMergeIntraCompanyFundsTransfers();

  const isMergedTransaction =
    isMergeIntraCompanyFundsTransfer &&
    paymentMethod === TransactionPaymentMethod.IntraCompanyFundsTransfer;

  const label = TransactionLabelBuilder.fromTransaction(transaction).build();

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box>
      <Box padding="space-16" paddingBottom={0}>
        <NavigationHeader
          backTo={getTransactionPath(transactionId)}
          closeTo={getRootPath()}
        />
      </Box>

      <HStack padding="space-16">
        <TransactionIcon
          isMergedTransaction={isMergedTransaction}
          size="md"
          transaction={transaction}
        />

        <Box flexGrow={1} paddingLeft="space-4">
          <Text noOfLines={1} variant="primary">
            {label}
          </Text>
          <Text variant="light">{formatDate(new Date(transactionAt), PP)}</Text>
        </Box>

        <TransactionAmount transaction={transaction} />
      </HStack>

      <Divider size="lg" />
      <SunshineCard
        alignItems="center"
        as={Link}
        isBoxed={false}
        padding="space-16"
        to="/help"
      >
        <SunshineCard.Slot name="aside">
          <SquircleIcon name="chat" />
        </SunshineCard.Slot>

        <SunshineCard.Content>
          <SunshineCard.Title>{locales.moreHelp}</SunshineCard.Title>
        </SunshineCard.Content>

        <SunshineCard.Slot name="action">
          <SunshineCard.SunshineIcon name="chevron-right" />
        </SunshineCard.Slot>
      </SunshineCard>

      {shouldDisplayDisputeButton ? (
        <Tooltip
          description={
            isAlreadyDisputed && alreadyDisputedAt
              ? locales.formatString(locales.alreadyDisputed, {
                  date: formatDate(new Date(alreadyDisputedAt), 'PPatp'),
                })
              : null
          }
          placement="bottom"
        >
          <SunshineCard
            alignItems="center"
            as={isAlreadyDisputed ? undefined : Link}
            isBoxed={false}
            isDisabled={isAlreadyDisputed}
            onClick={() => logEvent({ name: 'Click_Dispute_Transaction' })}
            padding="space-16"
            to={isAlreadyDisputed ? undefined : getDisputePath([transactionId])}
          >
            <SunshineCard.Slot name="aside">
              <SquircleIcon name="warning" />
            </SunshineCard.Slot>

            <SunshineCard.Content>
              <SunshineCard.Title>{locales.dispute}</SunshineCard.Title>
            </SunshineCard.Content>

            <SunshineCard.Slot name="action">
              <SunshineCard.SunshineIcon name="chevron-right" />
            </SunshineCard.Slot>
          </SunshineCard>
        </Tooltip>
      ) : null}
    </Box>
  );
};

export default memo(Help);
